import { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause, RotateCcw, Home, Download, Loader2, ArrowLeftCircle } from 'lucide-react';
import '@/App.css';
import JourneySelector from '@/components/JourneySelector';
import { exportJourneyToPdf } from '@/utils/exportPdf';

import AcellaDataScene from '@/components/scenes/AcellaDataScene';
import ProfileBuildingScene from '@/components/scenes/ProfileBuildingScene';
import InvitesScene from '@/components/scenes/InvitesScene';
import EmailInboxScene from '@/components/scenes/EmailInboxScene';
import OTPScene from '@/components/scenes/OTPScene';
import AcceptNominateScene from '@/components/scenes/AcceptNominateScene';
import ConfirmationScene from '@/components/scenes/ConfirmationScene';
import PortalDashboardScene from '@/components/scenes/PortalDashboardScene';
import UserFormBasicScene from '@/components/scenes/UserFormBasicScene';
import UserFormRolesScene from '@/components/scenes/UserFormRolesScene';
import UserFormReviewScene from '@/components/scenes/UserFormReviewScene';
import UserFormConfirmScene from '@/components/scenes/UserFormConfirmScene';
import UserCreatedScene from '@/components/scenes/UserCreatedScene';
import InviteReceivedScene from '@/components/scenes/InviteReceivedScene';
import NewUserSignInScene from '@/components/scenes/NewUserSignInScene';
import NewUserOTPScene from '@/components/scenes/NewUserOTPScene';
import NewUserConfirmProfileScene from '@/components/scenes/NewUserConfirmProfileScene';
import NewUserLoggedInScene from '@/components/scenes/NewUserLoggedInScene';
import CommunityStructureScene from '@/components/scenes/CommunityStructureScene';
import FacilityCategoryScene from '@/components/scenes/FacilityCategoryScene';
import ReportPortalScene from '@/components/scenes/ReportPortalScene';
import ReportFormFilledScene from '@/components/scenes/ReportFormFilledScene';
import ReportSubmittedScene from '@/components/scenes/ReportSubmittedScene';
import SurveyPersonasScene from '@/components/scenes/SurveyPersonasScene';
import SurveyBuildScene from '@/components/scenes/SurveyBuildScene';
import SurveyConfigScene from '@/components/scenes/SurveyConfigScene';
import SurveyReviewScene from '@/components/scenes/SurveyReviewScene';
import SurveyHubScene from '@/components/scenes/SurveyHubScene';
import SurveyBasicsScene from '@/components/scenes/SurveyBasicsScene';

// v2 — Onboarding Team 2 scenes (new design)
import UsersListV2Scene from '@/components/scenes/v2/UsersListV2Scene';
import UserFormBasicV2Scene from '@/components/scenes/v2/UserFormBasicV2Scene';
import UserFormRolesV2Scene from '@/components/scenes/v2/UserFormRolesV2Scene';
import UserFormReviewV2Scene from '@/components/scenes/v2/UserFormReviewV2Scene';
import UserFormConfirmV2Scene from '@/components/scenes/v2/UserFormConfirmV2Scene';
import UserCreatedV2Scene from '@/components/scenes/v2/UserCreatedV2Scene';
import CommunityStructureV2Scene from '@/components/scenes/v2/CommunityStructureV2Scene';
import FacilityCategoryV2Scene from '@/components/scenes/v2/FacilityCategoryV2Scene';
import FacilitiesListV2Scene from '@/components/scenes/v2/FacilitiesListV2Scene';
import FacilityDetailV2Scene from '@/components/scenes/v2/FacilityDetailV2Scene';
import ReportPortalV2Scene from '@/components/scenes/v2/ReportPortalV2Scene';
import ReportFormV2Scene from '@/components/scenes/v2/ReportFormV2Scene';
import ReportSubmittedV2Scene from '@/components/scenes/v2/ReportSubmittedV2Scene';

// Architecture journey scenes
import ArchIntroScene from '@/components/scenes/arch/ArchIntroScene';
import ArchThreeLayerScene from '@/components/scenes/arch/ArchThreeLayerScene';
import ArchStructureScene from '@/components/scenes/arch/ArchStructureScene';
import ArchCategoriesScene from '@/components/scenes/arch/ArchCategoriesScene';
import ArchRolesScene from '@/components/scenes/arch/ArchRolesScene';
import ArchAssignmentScene from '@/components/scenes/arch/ArchAssignmentScene';
import ArchScenariosScene from '@/components/scenes/arch/ArchScenariosScene';
import ArchStandardsScene from '@/components/scenes/arch/ArchStandardsScene';

const journeys = {
  community: {
    title: 'Community Structure',
    scenes: [
      { component: CommunityStructureScene, label: 'Community Structure' },
      { component: FacilityCategoryScene, label: 'Facility Categories' },
    ],
    durations: [12000, 12000],
  },
  acella: {
    title: 'Acella Onboarding',
    scenes: [
      { component: AcellaDataScene, label: 'Acella Data' },
      { component: ProfileBuildingScene, label: 'Profile Build' },
      { component: InvitesScene, label: 'Send Invites' },
      { component: EmailInboxScene, label: 'Email Inbox' },
      { component: OTPScene, label: 'OTP Verify' },
      { component: AcceptNominateScene, label: 'Accept / Nominate' },
      { component: ConfirmationScene, label: 'Confirmation' },
      { component: PortalDashboardScene, label: 'Portal Dashboard' },
    ],
    durations: [8000, 9000, 7000, 7000, 7000, 10000, 7000, 10000],
  },
  users: {
    title: 'User Management (full)',
    scenes: [
      { component: PortalDashboardScene, label: 'Users List' },
      { component: UserFormBasicScene, label: 'Basic Details' },
      { component: UserFormRolesScene, label: 'Assign Roles' },
      { component: UserFormReviewScene, label: 'Review & Add' },
      { component: UserFormConfirmScene, label: 'Confirm Email' },
      { component: UserCreatedScene, label: 'User Created' },
      { component: InviteReceivedScene, label: 'Invite Email' },
      { component: NewUserSignInScene, label: 'Sign In' },
      { component: NewUserOTPScene, label: 'Verify OTP' },
      { component: NewUserConfirmProfileScene, label: 'Confirm Profile' },
      { component: NewUserLoggedInScene, label: 'Portal Access' },
    ],
    durations: [8500, 16500, 10500, 7500, 6500, 10000, 8000, 8000, 8000, 9000, 10000],
  },
  um_add: {
    title: 'How to add a user',
    scenes: [
      { component: PortalDashboardScene, label: 'Users List' },
      { component: UserFormBasicScene, label: 'Basic Details' },
      { component: UserFormRolesScene, label: 'Assign Roles' },
      { component: UserFormReviewScene, label: 'Review & Add' },
      { component: UserFormConfirmScene, label: 'Confirm Email' },
      { component: UserCreatedScene, label: 'User Created' },
    ],
    durations: [8500, 16500, 10500, 7500, 6500, 8000],
  },
  um_facility: {
    title: 'How to assign facility',
    scenes: [
      { component: PortalDashboardScene, label: 'Users List' },
      { component: UserFormBasicScene, label: 'Facility Selection', props: { facilityFocus: true } },
    ],
    durations: [8500, 18000],
  },
  um_roles: {
    title: 'How to assign roles',
    scenes: [
      { component: PortalDashboardScene, label: 'Users List' },
      { component: UserFormBasicScene, label: 'User Context', props: { quickFill: true } },
      { component: UserFormRolesScene, label: 'Assign Roles' },
    ],
    durations: [8500, 6000, 12000],
  },
  report: {
    title: 'Report Issue',
    scenes: [
      { component: ReportPortalScene, label: 'Portal Home' },
      { component: ReportFormFilledScene, label: 'Fill & Submit' },
      { component: ReportSubmittedScene, label: 'Submitted' },
    ],
    durations: [9000, 14000, 8000],
  },
  survey: {
    title: 'Survey Creation',
    scenes: [
      { component: SurveyPersonasScene, label: 'Personas' },
      { component: SurveyHubScene, label: 'Survey Hub' },
      { component: SurveyBasicsScene, label: 'Basics' },
      { component: SurveyBuildScene, label: 'Build Questions' },
      { component: SurveyConfigScene, label: 'Configure' },
      { component: SurveyReviewScene, label: 'Review & Submit' },
    ],
    durations: [10000, 10000, 9000, 12000, 12000, 10000],
  },

  /* ==========================================================
   *  Onboarding Team 2 — same workflows, new portal design
   * ========================================================== */
  um2_add: {
    title: 'How to add a user (New design)',
    scenes: [
      { component: UsersListV2Scene,       label: 'Users List' },
      { component: UserFormBasicV2Scene,   label: 'Basic Details' },
      { component: UserFormRolesV2Scene,   label: 'Assign Roles' },
      { component: UserFormReviewV2Scene,  label: 'Review & Add' },
      { component: UserFormConfirmV2Scene, label: 'Sign-in Link Sent' },
      { component: UserCreatedV2Scene,     label: 'User Created' },
    ],
    durations: [8500, 16500, 10500, 7500, 6500, 8000],
  },
  um2_facility: {
    title: 'How to assign facility (New design)',
    scenes: [
      { component: UsersListV2Scene,     label: 'Users List' },
      { component: UserFormBasicV2Scene, label: 'Facility Selection', props: { facilityFocus: true } },
    ],
    durations: [8500, 18000],
  },
  um2_roles: {
    title: 'How to assign roles (New design)',
    scenes: [
      { component: UsersListV2Scene,     label: 'Users List' },
      { component: UserFormBasicV2Scene, label: 'User Context', props: { quickFill: true } },
      { component: UserFormRolesV2Scene, label: 'Assign Roles' },
    ],
    durations: [8500, 6000, 12000],
  },
  community_2: {
    title: 'Org Structure (New design)',
    scenes: [
      { component: CommunityStructureScene,   label: 'Community Structure' },
      { component: FacilityCategoryScene,     label: 'Facility Categories' },
      { component: FacilitiesListV2Scene,     label: 'Facilities List' },
      { component: FacilityDetailV2Scene,     label: 'Facility Detail' },
    ],
    durations: [12000, 12000, 9000, 10000],
  },
  report_2: {
    title: 'Report Issue (New design)',
    scenes: [
      { component: ReportPortalV2Scene,    label: 'Portal Home' },
      { component: ReportFormV2Scene,      label: 'Fill & Submit' },
      { component: ReportSubmittedV2Scene, label: 'Submitted' },
    ],
    durations: [9000, 14000, 8000],
  },

  /* ==========================================================
   *  User Management Architecture — interactive walkthrough
   *  of the Eclaims_UserManagement_Architecture document.
   * ========================================================== */
  user_mgmt_arch: {
    title: 'User Management Architecture',
    scenes: [
      { component: ArchIntroScene,       label: 'Three Pillars' },
      { component: ArchThreeLayerScene,  label: 'Architecture Diagram' },
      { component: ArchStructureScene,   label: 'Structural Hierarchy' },
      { component: ArchCategoriesScene,  label: 'Facility Categories' },
      { component: ArchRolesScene,       label: 'Role Catalog' },
      { component: ArchAssignmentScene,  label: 'Assignment Model' },
      { component: ArchScenariosScene,   label: 'Permitted Scenarios' },
      { component: ArchStandardsScene,   label: 'Standards & Compliance' },
    ],
    durations: [9000, 9000, 9000, 9000, 10000, 9000, 10000, 10000],
  },
};

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir < 0 ? 80 : -80, opacity: 0 }),
};

function App() {
  const [activeJourney, setActiveJourney] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Stack snapshot of the selector level from which this journey was launched.
  // Used to return the user to the correct section (e.g. "Onboarding Team 2 →
  // User Management") instead of all the way to the root selector.
  const [selectorStack, setSelectorStack] = useState([]);

  const journey = activeJourney ? journeys[activeJourney] : null;
  const scenes = journey?.scenes || [];

  const goTo = useCallback((step) => {
    if (step < 0 || step >= scenes.length) return;
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
  }, [currentStep, scenes.length]);

  const next = useCallback(() => {
    if (currentStep < scenes.length - 1) {
      setDirection(1);
      setCurrentStep(s => s + 1);
    } else {
      setIsPlaying(false);
    }
  }, [currentStep, scenes.length]);

  const prev = useCallback(() => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep(s => s - 1);
    }
  }, [currentStep]);

  const restart = useCallback(() => {
    setDirection(-1);
    setCurrentStep(0);
    setIsPlaying(true);
  }, []);

  const goHome = useCallback(() => {
    setIsPlaying(false);
    setActiveJourney(null);
    setCurrentStep(0);
    setDirection(1);
    setSelectorStack([]);
  }, []);

  // Return to the selector level the user launched from (the section card
  // they last clicked into — e.g. "Onboarding Team 2 → User Management").
  const goBackToSection = useCallback(() => {
    setIsPlaying(false);
    setActiveJourney(null);
    setCurrentStep(0);
    setDirection(1);
    // selectorStack is preserved so JourneySelector renders that level
  }, []);

  const handleSelectJourney = useCallback((key, stackSnapshot = []) => {
    setActiveJourney(key);
    setCurrentStep(0);
    setDirection(1);
    setIsPlaying(false);
    setSelectorStack(stackSnapshot);
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    if (!journey || isExporting) return;
    setIsPlaying(false);
    setIsExporting(true);
    const originalStep = currentStep;
    try {
      await exportJourneyToPdf({
        journeyTitle: journey.title,
        scenes,
        gotoStep: (i) => { setDirection(i > originalStep ? 1 : -1); setCurrentStep(i); },
        stepDelayMs: 1800,
      });
    } catch (e) {
      console.error('PDF export failed', e);
    } finally {
      setCurrentStep(originalStep);
      setIsExporting(false);
    }
  }, [journey, scenes, currentStep, isExporting]);

  useEffect(() => {
    if (!isPlaying || !journey) return;
    const d = journey.durations || [];
    const timer = setTimeout(next, d[currentStep] || 8000);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, next, journey]);

  useEffect(() => {
    if (!activeJourney) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') { setIsPlaying(false); next(); }
      if (e.key === 'ArrowLeft') { setIsPlaying(false); prev(); }
      if (e.key === ' ') { e.preventDefault(); setIsPlaying(p => !p); }
      if (e.key === 'Escape') { goHome(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, activeJourney, goHome]);

  // Journey selector (home)
  if (!activeJourney) {
    return (
      <div className="walkthrough-root" data-testid="walkthrough-root">
        <JourneySelector onSelect={handleSelectJourney} initialStack={selectorStack} />
      </div>
    );
  }

  const CurrentScene = scenes[currentStep]?.component;
  const progress = ((currentStep + 1) / scenes.length) * 100;

  return (
    <div className="walkthrough-root" data-testid="walkthrough-root">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-[#E5E7EB]">
        <motion.div className="h-full bg-teal-600" animate={{ width: `${progress}%` }} transition={{ duration: 0.4, ease: 'easeOut' }} data-testid="progress-bar" />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-white/80 border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={goHome} data-testid="home-button" className="w-7 h-7 rounded-lg bg-teal-600 flex items-center justify-center hover:bg-teal-700 transition-colors" title="Back to all journeys">
              <Home size={14} className="text-white" />
            </button>
            {selectorStack.length > 0 && (
              <button
                onClick={goBackToSection}
                data-testid="back-to-section-button"
                title="Back to section"
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-[#E5E7EB] text-[11px] font-medium text-[#0A0A0A] hover:bg-[#F3F4F6] transition-colors"
              >
                <ArrowLeftCircle size={12} />
                Back to section
              </button>
            )}
            <span className="font-heading font-bold text-sm text-[#0A0A0A] tracking-tight">Shafafiya</span>
            <span className="text-[11px] font-body px-2 py-0.5 rounded-md bg-teal-600/10 text-teal-700 font-medium">{journey.title}</span>
          </div>
          <div className="flex items-center gap-2 font-body text-sm text-[#737373]">
            <button
              data-testid="download-pdf-button"
              onClick={handleDownloadPdf}
              disabled={isExporting}
              title="Download journey as PDF"
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-teal-600 text-white text-[11px] font-medium hover:bg-teal-700 disabled:opacity-60 disabled:cursor-wait transition-colors"
            >
              {isExporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              {isExporting ? 'Exporting…' : 'PDF'}
            </button>
            <span className="text-[#0A0A0A] font-medium text-sm ml-1">{currentStep + 1}</span>
            <span className="text-xs">/</span>
            <span className="text-xs">{scenes.length}</span>
            <span className="ml-1 text-[11px] px-2 py-0.5 rounded-md bg-[#FAFAFA] border border-[#E5E7EB]">{scenes[currentStep]?.label}</span>
          </div>
        </div>
      </header>

      {/* Scene */}
      <main className="scene-container">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div key={currentStep} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="scene-wrapper">
            {CurrentScene && <CurrentScene onNext={next} {...(scenes[currentStep]?.props || {})} />}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Bottom Controls */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl bg-white/80 border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          {/* Timeline dots */}
          <div className="flex items-center gap-2" data-testid="timeline-dots">
            {scenes.map((scene, i) => (
              <button key={i} onClick={() => { setIsPlaying(false); goTo(i); }} data-testid={`timeline-dot-${i}`} className="group relative" title={scene.label}>
                <div className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  i === currentStep ? 'bg-teal-600 scale-125' : i < currentStep ? 'bg-teal-600/40' : 'bg-[#E5E7EB]'
                }`} />
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 rounded text-[10px] font-body bg-[#0A0A0A] text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  {scene.label}
                </span>
              </button>
            ))}
          </div>

          {/* Nav buttons */}
          <div className="flex items-center gap-2">
            <button data-testid="prev-button" onClick={() => { setIsPlaying(false); prev(); }} disabled={currentStep === 0}
              className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#FAFAFA] hover:bg-[#E5E7EB] disabled:opacity-30 transition-colors">
              <ChevronLeft size={18} className="text-[#0A0A0A]" />
            </button>
            <button data-testid="play-pause-button" onClick={() => setIsPlaying(p => !p)}
              className="w-10 h-10 rounded-xl flex items-center justify-center bg-teal-600 text-white hover:bg-teal-700 transition-colors">
              {isPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
            </button>
            {currentStep === scenes.length - 1 ? (
              <button data-testid="restart-button" onClick={restart}
                className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#FAFAFA] hover:bg-[#E5E7EB] transition-colors">
                <RotateCcw size={16} className="text-[#0A0A0A]" />
              </button>
            ) : (
              <button data-testid="next-button" onClick={() => { setIsPlaying(false); next(); }} disabled={currentStep === scenes.length - 1}
                className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#FAFAFA] hover:bg-[#E5E7EB] disabled:opacity-30 transition-colors">
                <ChevronRight size={18} className="text-[#0A0A0A]" />
              </button>
            )}
            {currentStep === scenes.length - 1 && (
              <button data-testid="back-to-home-button" onClick={selectorStack.length > 0 ? goBackToSection : goHome}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-teal-600 text-white text-xs font-body font-bold hover:bg-teal-700 transition-colors ml-2">
                {selectorStack.length > 0 ? <><ArrowLeftCircle size={14} /> Back to section</> : <><Home size={14} /> Select Journey</>}
              </button>
            )}
          </div>

          {/* Keyboard hint */}
          <div className="hidden md:flex items-center gap-3 text-[10px] font-body text-[#737373]">
            <span className="px-1.5 py-0.5 rounded bg-[#FAFAFA] border border-[#E5E7EB]">Space</span>
            <span>Play/Pause</span>
            <span className="px-1.5 py-0.5 rounded bg-[#FAFAFA] border border-[#E5E7EB]">&larr; &rarr;</span>
            <span>Navigate</span>
            <span className="px-1.5 py-0.5 rounded bg-[#FAFAFA] border border-[#E5E7EB]">Esc</span>
            <span>Home</span>
          </div>
        </div>
      </nav>
    </div>
  );
}

export default App;
